import { GrayDeleteButton } from "@components/buttons/gray-delete";
import { PlusButton } from "@components/buttons/plus";
import { RollBackButton } from "@components/buttons/rollback";
import {
  SHBox,
  SHButton,
  SHDataGrid,
  SHDataGridRef,
  SHStack,
} from "@components/design-systems";
import { DefaultDataState } from "@components/design-systems/sh-data-grid/constant";
import { EditSVG } from "@components/svgs";
import { PageRoutes } from "@constants";
import { TopBar, TopBarContainer } from "@layouts/top-bar";
import { PageMode, ParentState } from "@models/core";
import { ReadClientDTO } from "@models/reviews/entities/client";
import { ClientStatus } from "@models/reviews/enums/status";
import { useTheme } from "@mui/material";
import { TableState } from "@tanstack/react-table";
import { useMemo, useRef, useState } from "react";
import { generatePath, useNavigate } from "react-router";
import { useReviewClient } from "@hooks/useReviewClient";
import {
  FamilyGroupNameOfFactory as nameOf,
  getFamilyGroupTableColumnDefs,
} from "@pages/platform-analysis-v2/_client/utils";
import { FamilyGroupConfigs as Configs } from "@pages/platform-analysis-v2/_client/configs";

export const PlatformAnalysisClientList = () => {
  const { palette } = useTheme();
  const navigate = useNavigate();
  const { updateReviewClientStatus } = useReviewClient();

  const ref = useRef<SHDataGridRef | null>(null);

  const [updatingStatusIds, setUpdatingStatusIds] = useState<string[]>([]);
  const [gridState, setGridState] = useState<Partial<TableState>>({
    ...DefaultDataState,
    sorting: [{ id: nameOf("name"), desc: false }],
    columnFilters: [
      {
        id: nameOf("status"),
        value: [ClientStatus.Active],
      },
    ],
  });

  const handelOnChangeStatus = async (
    familyGroupId: string,
    status: ClientStatus,
  ) => {
    setUpdatingStatusIds((ids) => [...ids, familyGroupId]);
    await updateReviewClientStatus(familyGroupId, status);
    setUpdatingStatusIds((ids) => ids.filter((id) => id !== familyGroupId));
    ref.current?.refreshOdata();
  };

  const renderItemActions = (itemData: ReadClientDTO): JSX.Element => {
    return (
      <SHStack direction="row" justifyContent={"flex-start"} spacing={0.5}>
        <SHButton
          sx={{ width: "50%", fontSize: "13px" }}
          startIcon={<EditSVG color={palette.common.white} />}
          variant="contained"
          disabled={false}
          onClick={() => {
            navigate(
              generatePath(PageRoutes.platformAnalysisFamilyGroupDetail, {
                familyGroupId: itemData.id,
              }),
              { state: { pageMode: PageMode.Edit } as ParentState },
            );
          }}
        >
          Edit
        </SHButton>
        {itemData.status === ClientStatus.Archived ? (
          <RollBackButton
            disabled={updatingStatusIds.includes(itemData.id)}
            isLoading={updatingStatusIds.includes(itemData.id)}
            onClick={() =>
              handelOnChangeStatus(itemData.id, ClientStatus.Restore)
            }
          />
        ) : (
          <GrayDeleteButton
            disabled={updatingStatusIds.includes(itemData.id)}
            isLoading={updatingStatusIds.includes(itemData.id)}
            onClick={() =>
              handelOnChangeStatus(itemData.id, ClientStatus.Archived)
            }
          />
        )}
      </SHStack>
    );
  };

  const columns = useMemo(
    () =>
      getFamilyGroupTableColumnDefs({
        renderItemActions,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <>
      <TopBarContainer>
        <TopBar
          title={"Suitability Reviews"}
          tools={
            <>
              <PlusButton
                variant="contained"
                size="extraMedium"
                onClick={() =>
                  navigate(
                    generatePath(PageRoutes.platformAnalysisFamilyGroupDetail, {
                      familyGroupId: "new",
                    }),
                  )
                }
              >
                New family group
              </PlusButton>
            </>
          }
        />
      </TopBarContainer>
      <SHBox sx={{ marginTop: "25px" }}>
        <SHDataGrid
          ref={ref}
          odata={{
            url: Configs.DefaultODataRoute,
            pickColumns: Configs.PickColumns,
            globalFilterColumns: Configs.GlobalFilterColumns,
          }}
          state={gridState}
          onChangeState={(state) => setGridState(state)}
          emptyMessage={"No family groups."}
          columns={columns}
          searchBoxProps={{
            placeholder: "Family group name and adviser",
          }}
        />
      </SHBox>
    </>
  );
};
